import { lazy, Suspense } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { Navigate, RouterProvider, createBrowserRouter } from 'react-router-dom';
import { queryClient } from '@/lib/queryClient';
import { AuthProvider, useAuth } from '@/lib/auth-context';
import { AuthGuard } from '@/components/AuthGuard';
import { AppShell } from '@/components/AppShell';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Toaster } from '@/components/Toaster';
import { Spinner } from '@/components/ui/spinner';
import { commonT } from '@/i18n';

// Eager: home/login/privacy/terms/invite/events são pequenos e fazem parte do
// caminho frequente. Não compensa code-splitting que adicione round-trip
// na primeira navegação.
import { LoginPage } from '@/pages/Login';
import { EventsPage } from '@/pages/Events';
import { PrivacyPage } from '@/pages/Privacy';
import { TermsPage } from '@/pages/Terms';
import { InviteAcceptPage } from '@/pages/InviteAccept';
import { UsersPage } from '@/pages/Users';
import { TrackersPage } from '@/pages/Trackers';
import { BusinessPage } from '@/pages/Business';
import { ResultsIndexPage } from '@/pages/ResultsIndex';
import { AuthEmailLinkPage } from '@/pages/AuthEmailLink';
// LAZY, e não import estático (#966): esta página importa o `EventLiveMap`, que
// desde a migração pra MapLibre arrasta ~293 kB gzip. Import estático põe isso
// no grafo inicial de TODA rota — inclusive `/login` —, porque a rota é
// condicional mas a importação não, e o Rollup não consegue provar que o módulo
// é inalcançável em produção. Com Google eram 13 kB e ninguém notava.
const MarkerPreviewPage = lazy(() => import('@/pages/MarkerPreviewPage'));
const AthletesPreviewPage = lazy(() => import('@/pages/AthletesPreviewPage'));
const PodiumPreviewPage = lazy(() => import('@/pages/PodiumPreviewPage'));
const ResultsPreviewPage = lazy(() => import('@/pages/ResultsPreviewPage'));

// Lazy: wizard (≈6 telas com react-hook-form + Zod) e live (Google Maps
// + SignalR) compõem a maior parte do bundle. O viewer público anônimo
// não deve pagar pelo wizard inteiro pra ver um mapa.
const WizardLayout = lazy(() =>
  import('@/pages/wizard/WizardLayout').then((m) => ({ default: m.WizardLayout })),
);
const EventLiveAdminPage = lazy(() =>
  import('@/pages/live/EventLiveAdminPage').then((m) => ({ default: m.EventLiveAdminPage })),
);
const EventLivePublicPage = lazy(() =>
  import('@/pages/live/EventLivePublicPage').then((m) => ({ default: m.EventLivePublicPage })),
);
const EventReplayPage = lazy(() =>
  import('@/pages/live/EventReplayPage').then((m) => ({ default: m.EventReplayPage })),
);
const EventReplayPublicPage = lazy(() =>
  import('@/pages/live/EventReplayPage').then((m) => ({ default: m.EventReplayPublicPage })),
);
// #934 — página de resultados (lista completa). Lazy como as outras do módulo
// live, mas é a mais LEVE delas: não importa mapa nem SignalR, só o snapshot.
// Quem abre o link do resultado no celular não paga pelos ~293 kB do MapLibre.
const EventResultsPage = lazy(() =>
  import('@/pages/live/EventResultsPage').then((m) => ({ default: m.EventResultsPage })),
);
const EventResultsPublicPage = lazy(() =>
  import('@/pages/live/EventResultsPage').then((m) => ({ default: m.EventResultsPublicPage })),
);
// Replay de sessão do painel de negócio (#196) — carrega Google Maps, então
// lazy (o painel de listagem `/business` em si é eager e leve).
const BusinessSessionReplayPage = lazy(() =>
  import('@/pages/BusinessSessionReplay').then((m) => ({
    default: m.BusinessSessionReplayPage,
  })),
);
// Gerador de vídeo do percurso 3D. Lazy: traz o encoder de MP4, e só o
// super-admin abre.
const FlyoverGeneratorPage = lazy(() =>
  import('@/pages/FlyoverGenerator').then((m) => ({ default: m.FlyoverGeneratorPage })),
);
// #322 — buyflow da empresa de apoio DW (destino do CTA da landing). Lazy:
// quem usa o painel de eventos não paga pelo form + resize de imagem.
const ApoioPage = lazy(() => import('@/pages/Apoio').then((m) => ({ default: m.ApoioPage })));
// #525 — crescimento da base no painel de negócio. Lazy: é a única página que
// puxa recharts, e quem opera evento/sessão não paga pela lib de gráfico.
const BusinessGrowthPage = lazy(() =>
  import('@/pages/BusinessGrowth').then((m) => ({ default: m.BusinessGrowthPage })),
);
// #837 — inscrição do atleta pelo QR/link do evento. Pública (login Firebase
// dentro da própria página); lazy porque quem opera o painel não a abre.
const SignupPage = lazy(() => import('@/pages/Signup').then((m) => ({ default: m.SignupPage })));

function RouteFallback() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <Spinner className="h-6 w-6" />
    </div>
  );
}

/// Wrapper que aplica ErrorBoundary + Suspense em volta de cada rota
/// lazy. O ErrorBoundary recebe um `name` distintivo pra que o painel do
/// Sentry consiga filtrar por área quando aparecer regressão.
function LazyRoute({ name, children }: { name: string; children: React.ReactNode }) {
  return (
    <ErrorBoundary name={name}>
      <Suspense fallback={<RouteFallback />}>{children}</Suspense>
    </ErrorBoundary>
  );
}

const router = createBrowserRouter(
  [
    { path: '/', element: <IndexRedirect /> },
    // DEV-ONLY: preview dos markers do evento (mock). Só registrado em dev.
    ...(import.meta.env.DEV
      ? [
          {
            path: '/marker-preview',
            element: (
              <LazyRoute name="marker-preview">
                <MarkerPreviewPage />
              </LazyRoute>
            ),
          },
          // #932 — preview do passo Atletas com fixtures (sem backend/login).
          {
            path: '/athletes-preview',
            element: (
              <LazyRoute name="athletes-preview">
                <AthletesPreviewPage />
              </LazyRoute>
            ),
          },
          // #856 — preview do pódio com uma e com duas frotas no mesmo dia.
          {
            path: '/podium-preview',
            element: (
              <LazyRoute name="podium-preview">
                <PodiumPreviewPage />
              </LazyRoute>
            ),
          },
          // #934 — preview da página de resultados nos três estados de selo.
          {
            path: '/results-preview',
            element: (
              <LazyRoute name="results-preview">
                <ResultsPreviewPage />
              </LazyRoute>
            ),
          },
        ]
      : []),
    { path: '/privacy', element: <PrivacyPage /> },
    { path: '/terms', element: <TermsPage /> },
    { path: '/login', element: <LoginPage /> },
    { path: '/invite/accept', element: <InviteAcceptPage /> },
    // Magic link landing — quando cliente clica no link gerado por
    // /api/admin/users/invite. Sem AuthGuard (usuário ainda não está logado).
    { path: '/auth/email-link', element: <AuthEmailLinkPage /> },
    // #322 — buyflow da empresa de apoio (CTA "Cadastrar minha empresa" da
    // landing). SEM AuthGuard: a própria página trata o não-logado (pitch +
    // Google) pra não jogar quem chega da landing no login genérico do painel.
    {
      path: '/apoio',
      element: (
        <LazyRoute name="apoio-buyflow">
          <ApoioPage />
        </LazyRoute>
      ),
    },
    // #837/#912 — inscrição por QR/link. SEM AuthGuard, e agora sem login
    // nenhum: a página não inscreve, ela mostra o evento e encaminha pro app
    // (ADR-015 §6). É lida do cartaz por quem ainda não tem o app, e pelos
    // casos em que o sistema não entrega o link ao app instalado (in-app
    // browser do WhatsApp, URL colada no Chrome).
    {
      path: '/inscricao/:token',
      element: (
        <LazyRoute name="signup">
          <SignupPage />
        </LazyRoute>
      ),
    },
    // F1.18 — viewer público de prova ao vivo. SEM AuthGuard.
    {
      path: '/live',
      element: (
        <LazyRoute name="live-public">
          <EventLivePublicPage />
        </LazyRoute>
      ),
    },
    // Replay pro espectador. SEM AuthGuard — o gate real é server-side: o
    // backend só serve o replay de um dia (ou evento) ENCERRADO.
    // **Fora de `/live/`** de propósito: o nginx tem um `location ^~ /live/`
    // que serve o viewer vanilla LEGADO do DW (links vivos em WhatsApp) e
    // engoliria qualquer sub-rota da SPA ali dentro.
    {
      path: '/replay',
      element: (
        <LazyRoute name="replay-public">
          <EventReplayPublicPage />
        </LazyRoute>
      ),
    },
    // #934 — resultados pro espectador e pro atleta. SEM AuthGuard: o gate é
    // server-side, o mesmo do `/live` (o snapshot público já chega sem posição
    // quando `standings_visible=false` ou o dia está provisório com
    // `hideUntilOfficial`). **Fora de `/live/`** pela mesma razão do `/replay`:
    // o nginx tem um `location ^~ /live/` que serve o viewer vanilla legado.
    {
      path: '/results',
      element: (
        <LazyRoute name="results-public">
          <EventResultsPublicPage />
        </LazyRoute>
      ),
    },
    // #936 — telas de gestão dentro do menu lateral (`AppShell`). A rota-pai
    // só exige login; **cada filha mantém o próprio `AuthGuard`** com o papel
    // de sempre. O menu esconder "Gestão" não é o gate: `/business` digitado
    // na URL por organizador não verificado continua barrado aqui, e a
    // mensagem de acesso restrito aparece com o menu em volta.
    // Wizard, live e replays ficam FORA (telas de foco, com "Voltar" próprio).
    {
      element: (
        <AuthGuard>
          <AppShell />
        </AuthGuard>
      ),
      children: [
        {
          path: '/events',
          element: (
            <AuthGuard requireRole="organizer">
              <ErrorBoundary name="events-list">
                <EventsPage />
              </ErrorBoundary>
            </AuthGuard>
          ),
        },
        // #936 — destino do item "Resultados": escolher o evento. Estático,
        // então o router o prefere ao `/events/:eventId/...` sem ambiguidade.
        {
          path: '/events/results',
          element: (
            <AuthGuard requireRole="organizer">
              <ErrorBoundary name="results-index">
                <ResultsIndexPage />
              </ErrorBoundary>
            </AuthGuard>
          ),
        },
        // #934 — resultados do organizador. Mesma montagem da pública, mas com o
        // `ranking` da rota admin autenticada: é ele que repõe a colocação quando o
        // snapshot público vem cortado por visibilidade (#817).
        {
          path: '/events/:eventId/results',
          element: (
            <AuthGuard requireRole="organizer">
              <LazyRoute name="results-admin">
                <EventResultsPage />
              </LazyRoute>
            </AuthGuard>
          ),
        },
        // Bloco 1b+2 — gestão de usuários pelo super-admin. Eager (não lazy)
        // pq é interno e pequeno; lazy só compensa nas telas pesadas (wizard/live).
        {
          path: '/users',
          element: (
            <AuthGuard requireRole="verified_organizer">
              <ErrorBoundary name="users-admin">
                <UsersPage />
              </ErrorBoundary>
            </AuthGuard>
          ),
        },
        // Inventário de rastreadores (#1172). Mesmo gate e mesmo motivo do
        // eager de `/users`: tela interna e leve.
        {
          path: '/trackers',
          element: (
            <AuthGuard requireRole="verified_organizer">
              <ErrorBoundary name="trackers-admin">
                <TrackersPage />
              </ErrorBoundary>
            </AuthGuard>
          ),
        },
        // #196 — Painel de negócio (sessões/DWs/eventos, fonte Postgres). Gate
        // `verified_organizer` (super-admin) — trajeto GPS é PII de qualquer atleta.
        // Listagem eager (leve); replay lazy (Google Maps).
        {
          path: '/business',
          element: (
            <AuthGuard requireRole="verified_organizer">
              <ErrorBoundary name="business-panel">
                <BusinessPage />
              </ErrorBoundary>
            </AuthGuard>
          ),
        },
        // #525 — crescimento da base (novos/semana, ativos, ativação). Mesmo gate
        // do resto do painel de negócio.
        {
          path: '/business/growth',
          element: (
            <AuthGuard requireRole="verified_organizer">
              <LazyRoute name="business-growth">
                <BusinessGrowthPage />
              </LazyRoute>
            </AuthGuard>
          ),
        },
        // Gerador de vídeo do percurso 3D. Gate `verified_organizer`: o vídeo
        // usa o trajeto de todos os atletas da categoria (PII), igual ao
        // painel de negócio.
        {
          path: '/flyover',
          element: (
            <AuthGuard requireRole="verified_organizer">
              <LazyRoute name="flyover-generator">
                <FlyoverGeneratorPage />
              </LazyRoute>
            </AuthGuard>
          ),
        },
      ],
    },
    {
      path: '/business/sessions/:id',
      element: (
        <AuthGuard requireRole="verified_organizer">
          <LazyRoute name="business-session-replay">
            <BusinessSessionReplayPage />
          </LazyRoute>
        </AuthGuard>
      ),
    },
    {
      // Wizard quando ainda não há evento (passo 1).
      path: '/events/new',
      element: (
        <AuthGuard requireRole="organizer">
          <LazyRoute name="wizard-new">
            <WizardLayout />
          </LazyRoute>
        </AuthGuard>
      ),
    },
    {
      path: '/events/:eventId/wizard',
      element: (
        <AuthGuard requireRole="organizer">
          <LazyRoute name="wizard">
            <WizardLayout />
          </LazyRoute>
        </AuthGuard>
      ),
    },
    // F1.14 — painel ao vivo do organizador (raceDay opcional; sem ele,
    // página usa o primeiro raceDay do evento).
    {
      path: '/events/:eventId/race/:raceDayId/live',
      element: (
        <AuthGuard requireRole="organizer">
          <LazyRoute name="live-admin">
            <EventLiveAdminPage />
          </LazyRoute>
        </AuthGuard>
      ),
    },
    {
      path: '/events/:eventId/live',
      element: (
        <AuthGuard requireRole="organizer">
          <LazyRoute name="live-admin">
            <EventLiveAdminPage />
          </LazyRoute>
        </AuthGuard>
      ),
    },
    // F3 (#67) — replay completo de prova (player). Organizer only.
    {
      path: '/events/:eventId/race/:raceDayId/replay',
      element: (
        <AuthGuard requireRole="organizer">
          <LazyRoute name="replay">
            <EventReplayPage />
          </LazyRoute>
        </AuthGuard>
      ),
    },
    { path: '*', element: <NotFoundRoute /> },
  ],
  { basename: '/' },
);

/// Raiz do painel. Não existe landing própria — quem já está logado cai
/// direto na lista de eventos; quem não está vai pro login (a tela inicial
/// de fato). Espera o estado de auth resolver antes de decidir pra não
/// piscar /login pra um organizer já autenticado.
function IndexRedirect() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }
  return <Navigate to={user ? '/events' : '/login'} replace />;
}

/// #936 — NotFound mantém o menu pra quem está logado (dá pra sair do erro
/// pelo menu). Mesma regra das rotas do shell: qualquer login ganha o shell, e
/// o próprio menu mostra só as seções que o papel permite (`navSectionsFor`);
/// sem papel de organizador, sobra o rodapé com o "Sair".
/// Não é filha da rota do shell de propósito: lá o `AuthGuard` mandaria o
/// visitante anônimo pro login por causa de um link quebrado.
function NotFoundRoute() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }
  if (user) {
    return (
      <AppShell>
        <NotFound />
      </AppShell>
    );
  }
  return <NotFound />;
}

function NotFound() {
  const t = commonT();
  return (
    <div className="container mx-auto py-24 text-center">
      <h2 className="text-2xl font-semibold">{t.notFoundTitle}</h2>
      <p className="mt-2 text-muted-foreground">{t.notFoundBody}</p>
    </div>
  );
}

export function App() {
  return (
    <ErrorBoundary name="root">
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <RouterProvider router={router} />
          <Toaster />
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
